import { useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, MessageSquare, Search, X, Wallet, CalendarClock } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import {
  formatBalance,
  useSendSms,
  useSmsClinic,
  useSmsCreditTransactions,
  useSmsLogs,
  useSmsLogsRealtime,
  useAppointmentSmsJobs,
  type SmsLogStatus,
  type SmsLogRow,
  type SmsLogsFilters,
  type AppointmentSmsJobRow,
} from '@/hooks/useSms';
import {
  remainingSmsSegments,
  smsReasonLabelKey,
  smsStatusLabelKey,
  smsTriggerLabelKey,
} from '@/lib/smsCompose';
import {
  useCancelSmsBundleOrder,
  useRequestSmsBundleOrder,
  useSmsBundleOrders,
  useSmsBundleProducts,
  useSmsSegmentPrice,
} from '@/hooks/useSmsBilling';
import { Button } from '@/components/ui/Button';
import { STATUS_BADGE, TABLE_CELL, TABLE_CELL_MUTED, TABLE_HEADER } from '@/components/ui/pageChrome';
import { TYPE, UI_MUTED, UI_SECONDARY, UI_SECTION } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { Input } from '@/components/ui/Input';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { ApiCallError } from '@/lib/api';

const STATUSES: Array<SmsLogStatus | 'all'> = ['all', 'queued', 'sent', 'delivered', 'failed'];

export function Sms() {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { isClinicAdmin, isPlatformAdmin } = useAuth();

  const [statusFilter, setStatusFilter] = useState<SmsLogStatus | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const filters = useMemo<SmsLogsFilters>(
    () => ({
      status: statusFilter,
      search: searchTerm.trim() || undefined,
    }),
    [statusFilter, searchTerm],
  );

  const clinicQuery = useSmsClinic(activeClinicId);
  const logsQuery = useSmsLogs(activeClinicId, filters);
  const txnsQuery = useSmsCreditTransactions(activeClinicId, 20);
  const jobsQuery = useAppointmentSmsJobs(activeClinicId, 50);
  const bundlesQuery = useSmsBundleProducts();
  const ordersQuery = useSmsBundleOrders(activeClinicId ?? null);
  const priceQuery = useSmsSegmentPrice();
  const requestOrder = useRequestSmsBundleOrder();
  const cancelOrder = useCancelSmsBundleOrder();

  useSmsLogsRealtime(activeClinicId);

  const canBuy = activeClinicId ? isPlatformAdmin || isClinicAdmin(activeClinicId) : false;
  const priceCents = priceQuery.data ?? 25;

  const credits = clinicQuery.data?.credits;
  const settings = clinicQuery.data?.settings;
  const logs = logsQuery.data ?? [];
  const txns = txnsQuery.data ?? [];
  const jobs = jobsQuery.data ?? [];

  const lowBalance =
    credits != null &&
    credits.lowBalanceThreshold > 0 &&
    credits.balance <= credits.lowBalanceThreshold;

  return (
    <div className="space-y-6">
          {/* Balance + status cards */}
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="rounded-xl border border-ds-line bg-ds-card p-5 shadow-card">
              <div className="flex items-center gap-2">
                <Wallet className="h-5 w-5 text-ds-teal" />
                <h3 className={`${TYPE.label} text-ds-text`}>
                  {t('sms.balance')}
                </h3>
              </div>
              <p className="mt-2 text-2xl font-semibold text-ds-text">
                {credits ? formatBalance(credits.balance) : '—'}
              </p>
              {credits && (
                <p className={`mt-1 ${UI_SECONDARY}`}>
                  {remainingSmsSegments(credits.balance, priceCents)} {t('sms.segments')} {t('sms.remaining')}
                </p>
              )}
              {lowBalance && (
                <p className={`mt-1 ${TYPE.small} text-ds-red-ink`}>
                  {t('sms.low_balance_warning')}
                </p>
              )}
              {credits && (
                <p className={`mt-1 ${UI_SECONDARY}`}>
                  {t('sms.usage_summary', {
                    messages: credits.lifetimeUsedMessages,
                    segments: credits.lifetimeUsedSegments,
                    bought: credits.lifetimePurchasedSegments,
                  })}
                </p>
              )}
            </div>

            <div className="rounded-xl border border-ds-line bg-ds-card p-5 shadow-card">
              <h3 className={`${TYPE.label} text-ds-text`}>
                {t('sms.sender_label')}
              </h3>
              <p className="mt-2 truncate text-lg font-semibold text-ds-text">
                {settings?.sender ?? 'Clinio'}
              </p>
              <p className={`mt-1 ${UI_SECONDARY}`}>
                {settings?.enabled ? t('sms.enabled') : t('sms.disabled')}
              </p>
            </div>

            <div className="rounded-xl border border-ds-line bg-ds-card p-5 shadow-card">
              <h3 className={`${TYPE.label} text-ds-text`}>
                {t('sms.recent_log_count')}
              </h3>
              <p className="mt-2 text-2xl font-semibold text-ds-text">
                {logs.length}
              </p>
              <p className={`mt-1 ${UI_SECONDARY}`}>
                {t('sms.recent_log_hint')}
              </p>
            </div>
          </div>

          {canBuy && (
            <section className="rounded-xl border border-ds-line bg-ds-card p-5 shadow-card">
              <h3 className={`${TYPE.label} text-ds-text`}>{t('sms.buy_bundle_title')}</h3>
              <p className={`mt-1 ${UI_SECONDARY}`}>{t('sms.buy_bundle_desc')}</p>
              <div className="mt-4 grid gap-3 md:grid-cols-3">
                {(bundlesQuery.data ?? []).filter((b) => b.is_active).map((bundle) => (
                  <div key={bundle.id} className="rounded-lg border border-ds-line bg-ds-sunk p-4">
                    <p className="font-semibold text-ds-text">{bundle.name_da}</p>
                    <p className="mt-1 text-sm text-ds-muted">
                      {bundle.segments} {t('sms.segments')}
                    </p>
                    <p className="mt-2 text-lg font-semibold text-ds-text">{formatBalance(bundle.price_cents)}</p>
                    <Button
                      type="button"
                      className="mt-3"
                      size="sm"
                      disabled={!activeClinicId || requestOrder.isPending}
                      onClick={async () => {
                        if (!activeClinicId) return;
                        try {
                          await requestOrder.mutateAsync({
                            clinic_id: activeClinicId,
                            bundle_id: bundle.id,
                          });
                          toast.success(t('sms.order_placed'));
                        } catch {
                          toast.error(t('sms.order_failed'));
                        }
                      }}
                    >
                      {t('sms.order_bundle')}
                    </Button>
                  </div>
                ))}
              </div>
              {(ordersQuery.data ?? []).length > 0 && (
                <div className="mt-6 overflow-x-auto">
                  <table className="min-w-full divide-y divide-ds-line">
                    <thead className="bg-ds-sunk">
                      <tr>
                        <th className={`${TABLE_HEADER} text-left`}>{t('sms.col_when')}</th>
                        <th className={`${TABLE_HEADER} text-left`}>{t('sms.col_segments')}</th>
                        <th className={`${TABLE_HEADER} text-left`}>{t('sms.col_cost')}</th>
                        <th className={`${TABLE_HEADER} text-left`}>{t('sms.col_status')}</th>
                        <th className={`${TABLE_HEADER} text-left`} />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-ds-line">
                      {(ordersQuery.data ?? []).map((order) => (
                        <tr key={order.id}>
                          <td className={TABLE_CELL}>
                            {new Date(order.created_at).toLocaleString('da-DK', {
                              timeZone: 'Europe/Copenhagen',
                            })}
                          </td>
                          <td className={TABLE_CELL}>{order.segments}</td>
                          <td className={TABLE_CELL}>{formatBalance(order.price_cents)}</td>
                          <td className={TABLE_CELL}>{t(`sms.order_status_${order.status}`)}</td>
                          <td className={TABLE_CELL}>
                            {order.status === 'pending' && (
                              <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                onClick={async () => {
                                  try {
                                    await cancelOrder.mutateAsync({ order_id: order.id });
                                    toast.success(t('sms.order_cancelled'));
                                  } catch {
                                    toast.error(t('sms.order_failed'));
                                  }
                                }}
                              >
                                {t('common.cancel')}
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}

          {/* Filters */}
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <div className="max-w-md flex-1">
              <Input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('sms.search_placeholder')}
                leadingIcon={<Search className="h-4 w-4" aria-hidden />}
                trailingIcon={
                  searchTerm ? (
                    <button
                      type="button"
                      onClick={() => setSearchTerm('')}
                      className="text-ds-placeholder hover:text-ds-muted"
                      aria-label={t('sms.clear_search')}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  ) : undefined
                }
              />
            </div>

            <div className="inline-flex rounded-md border border-ds-field bg-ds-card shadow-card">
              {STATUSES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatusFilter(s)}
                  className={`px-3 py-1.5 text-sm first:rounded-l-md last:rounded-r-md ${
                    statusFilter === s
                      ? 'bg-ds-teal text-ds-on-accent'
                      : 'text-ds-text hover:bg-ds-neutral-tint'
                  }`}
                >
                  {t(`sms.status_${s === 'all' ? 'all' : s}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Logs */}
          {logsQuery.error && (
            <div className="mt-6">
              <ErrorAlert message={t('sms.load_error')} />
            </div>
          )}

          {logsQuery.isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-ds-muted" />
            </div>
          ) : logs.length === 0 ? (
            <EmptyLogs />
          ) : (
            <div className="mt-6 overflow-hidden sm:rounded-md">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-ds-line">
                  <thead className="bg-ds-sunk">
                    <tr>
                      <Th>{t('sms.col_when')}</Th>
                      <Th>{t('sms.col_to')}</Th>
                      <Th>{t('sms.col_trigger')}</Th>
                      <Th>{t('sms.col_status')}</Th>
                      <Th>{t('sms.col_parts')}</Th>
                      <Th>{t('sms.col_cost')}</Th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ds-line bg-ds-card">
                    {logs.map((log) => (
                      <LogRow key={log.id} log={log} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* SMS Automation jobs panel (MOD-9b) */}
          <section className="mt-10">
            <div className="flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-ds-teal" />
              <h3 className={UI_SECTION}>
                {t('sms.automation_title')}
              </h3>
            </div>
            <p className={`mt-1 ${UI_MUTED}`}>
              {t('sms.automation_subtitle')}
            </p>
            {jobsQuery.isLoading ? (
              <div className="mt-4 flex items-center gap-2 text-sm text-ds-muted">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('app.loading')}
              </div>
            ) : jobs.length === 0 ? (
              <p className="mt-4 text-sm text-ds-muted">
                {t('sms.automation_empty')}
              </p>
            ) : (
              <div className="mt-4 overflow-hidden sm:rounded-md">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-ds-line">
                    <thead className="bg-ds-sunk">
                      <tr>
                        <Th>{t('sms.col_trigger')}</Th>
                        <Th>{t('sms.col_scheduled')}</Th>
                        <Th>{t('sms.col_status')}</Th>
                        <Th>{t('sms.col_reason')}</Th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-ds-line bg-ds-card">
                      {jobs.map((job) => (
                        <AutomationJobRow key={job.id} job={job} />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>

          {/* Credit ledger (admin only) */}
          {isPlatformAdmin && activeClinicId && settings?.enabled && (
            <TestSmsCard clinicId={activeClinicId} />
          )}

          {canBuy && txns.length > 0 && (
            <section className="mt-10">
              <h3 className={UI_SECTION}>
                {t('sms.ledger_title')}
              </h3>
              <p className={`mt-1 ${UI_MUTED}`}>
                {t('sms.ledger_subtitle')}
              </p>
              <div className="mt-4 overflow-hidden sm:rounded-md">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-ds-line">
                    <thead className="bg-ds-sunk">
                      <tr>
                        <Th>{t('sms.col_when')}</Th>
                        <Th>{t('sms.col_kind')}</Th>
                        <Th>{t('sms.col_delta')}</Th>
                        <Th>{t('sms.col_balance_after')}</Th>
                        <Th>{t('sms.col_reason')}</Th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-ds-line bg-ds-card">
                      {txns.map((txn) => (
                        <tr key={txn.id}>
                          <td className="whitespace-nowrap px-3 py-3 text-sm text-ds-muted">
                            {new Date(txn.created_at).toLocaleString('da-DK', {
                              timeZone: 'Europe/Copenhagen',
                            })}
                          </td>
                          <td className="whitespace-nowrap px-3 py-3 text-sm">
                            <KindBadge kind={txn.kind} />
                          </td>
                          <td
                            className={`whitespace-nowrap px-3 py-3 text-sm font-medium ${
                              txn.delta_cents >= 0
                                ? 'text-ds-green-ink'
                                : 'text-ds-red-ink'
                            }`}
                          >
                            {txn.delta_cents >= 0 ? '+' : ''}
                            {formatBalance(txn.delta_cents)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-3 text-sm text-ds-text">
                            {formatBalance(txn.balance_after_cents)}
                          </td>
                          <td className="px-3 py-3 text-sm text-ds-muted">
                            {txn.reason ?? '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          )}

    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      scope="col"
      className={cn('py-3.5 pl-4 pr-3 text-left sm:pl-6', TABLE_HEADER)}
    >
      {children}
    </th>
  );
}

function StatusPill({ status }: { status: SmsLogStatus }) {
  const { t } = useTranslation();
  const colors: Record<SmsLogStatus, string> = {
    queued: 'bg-ds-neutral-tint text-ds-muted',
    sent: 'bg-ds-teal-tint text-ds-teal',
    delivered: 'bg-ds-green-tint text-ds-green-ink',
    failed: 'bg-ds-red-tint text-ds-red-ink',
    rejected: 'bg-ds-red-tint text-ds-red-ink',
    expired: 'bg-ds-amber-tint text-ds-amber-ink',
    unknown: 'bg-ds-neutral-tint text-ds-muted',
  };
  const label = smsStatusLabelKey(status);
  return (
    <span className={cn('inline-flex rounded-full px-2 py-0.5', STATUS_BADGE, colors[status])}>
      {label.key ? t(label.key) : label.fallback}
    </span>
  );
}

function KindBadge({ kind }: { kind: string }) {
  const { t } = useTranslation();
  const colors: Record<string, string> = {
    topup: 'bg-ds-green-tint text-ds-green-ink',
    reservation: 'bg-ds-teal-tint text-ds-teal',
    refund: 'bg-ds-amber-tint text-ds-amber-ink',
    capture: 'bg-ds-neutral-tint text-ds-muted',
    adjustment: 'bg-ds-teal-tint text-ds-teal',
  };
  return (
    <span
      className={cn('inline-flex rounded-full px-2 py-0.5', STATUS_BADGE, colors[kind] ?? colors.adjustment)}
    >
      {t(`sms.kind_${kind}`, kind)}
    </span>
  );
}

function LogRow({ log }: { log: SmsLogRow }) {
  const { t } = useTranslation();
  return (
    <tr>
      <td className={cn('whitespace-nowrap py-3 pl-4 pr-3 sm:pl-6', TABLE_CELL_MUTED)}>
        {new Date(log.created_at).toLocaleString('da-DK', {
          timeZone: 'Europe/Copenhagen',
        })}
      </td>
      <td className={cn('px-3 py-3', TABLE_CELL)}>
        <div className="font-mono text-ds-text">{log.to_phone}</div>
        {log.patient_name_snapshot && (
          <div className={UI_SECONDARY}>
            {log.patient_name_snapshot}
          </div>
        )}
      </td>
      <td className={cn('px-3 py-3', TABLE_CELL_MUTED)}>
        {(() => {
          const label = smsTriggerLabelKey(log.trigger);
          return label.key ? t(label.key) : label.fallback;
        })()}
      </td>
      <td className={cn('px-3 py-3', TABLE_CELL)}>
        <StatusPill status={log.status} />
        {log.error_code && (
          <div className={`mt-0.5 ${TYPE.small} text-ds-red-ink`}>{log.error_code}</div>
        )}
      </td>
      <td className={cn('px-3 py-3 text-ds-text', TABLE_CELL)}>{log.parts}</td>
      <td className={cn('px-3 py-3 text-ds-text', TABLE_CELL)}>
        {formatBalance(log.clinic_price_cents)}
      </td>
    </tr>
  );
}

function EmptyLogs() {
  const { t } = useTranslation();
  return (
    <div className="mt-6 rounded-xl border border-dashed border-ds-field bg-ds-card py-12 text-center">
      <MessageSquare className="mx-auto h-10 w-10 text-ds-muted" />
      <p className={`mt-3 ${UI_MUTED}`}>{t('sms.empty')}</p>
    </div>
  );
}

// =============================================================================
// AutomationJobRow
// =============================================================================

function JobStatusPill({ status }: { status: AppointmentSmsJobRow['status'] }) {
  const { t } = useTranslation();
  const colors: Record<AppointmentSmsJobRow['status'], string> = {
    pending: 'bg-ds-amber-tint text-ds-amber-ink',
    sent: 'bg-ds-green-tint text-ds-green-ink',
    skipped: 'bg-ds-neutral-tint text-ds-muted',
    failed: 'bg-ds-red-tint text-ds-red-ink',
  };
  const label = smsStatusLabelKey(status);
  return (
    <span className={cn('inline-flex rounded-full px-2 py-0.5', STATUS_BADGE, colors[status])}>
      {label.key ? t(label.key) : label.fallback}
    </span>
  );
}

function AutomationJobRow({ job }: { job: AppointmentSmsJobRow }) {
  const { t } = useTranslation();
  const trigger = smsTriggerLabelKey(job.trigger);
  const reason = smsReasonLabelKey(job.reason);
  return (
    <tr>
      <td className="whitespace-nowrap px-3 py-3 text-sm font-medium text-ds-text">
        {trigger.key ? t(trigger.key) : trigger.fallback}
      </td>
      <td className="whitespace-nowrap px-3 py-3 text-sm text-ds-muted">
        {job.scheduled_for
          ? new Date(job.scheduled_for).toLocaleString('da-DK', { timeZone: 'Europe/Copenhagen' })
          : '—'}
      </td>
      <td className="whitespace-nowrap px-3 py-3 text-sm">
        <JobStatusPill status={job.status} />
      </td>
      <td className="px-3 py-3 text-sm text-ds-muted">
        {reason.key ? t(reason.key) : reason.fallback}
      </td>
    </tr>
  );
}

function TestSmsCard({ clinicId }: { clinicId: string }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const send = useSendSms();
  const [body, setBody] = useState('');

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!user?.id) {
      toast.error(t('sms.test_no_profile_phone'));
      return;
    }
    try {
      const { data, error } = await supabase.from('users').select('phone').eq('id', user.id).maybeSingle();
      if (error || !data?.phone) {
        toast.error(t('sms.test_no_profile_phone'));
        return;
      }
      await send.mutateAsync({
        clinic_id: clinicId,
        body: body.trim(),
        trigger: 'test',
      });
      toast.success(t('sms.test_send_success'));
      setBody('');
    } catch (err) {
      const msg = err instanceof ApiCallError ? err.message : t('sms.send_failed');
      toast.error(msg);
    }
  }

  return (
    <section className="mt-10 rounded-xl border border-ds-line bg-ds-card p-5 shadow-card">
      <h3 className={UI_SECTION}>{t('sms.test_title')}</h3>
      <p className={`mt-1 ${UI_MUTED}`}>{t('sms.test_desc')}</p>
      <form className="mt-4 flex flex-col gap-3 md:flex-row md:items-end" onSubmit={handleSubmit}>
        <div className="min-w-0 flex-1">
          <label className="block text-sm font-medium text-ds-text">{t('sms.field_body')}</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={2}
            maxLength={160}
            required
            className="ds-field mt-1 block w-full"
          />
        </div>
        <Button type="submit" disabled={send.isPending || !body.trim()}>
          {send.isPending ? t('common.saving') : t('sms.test_send')}
        </Button>
      </form>
    </section>
  );
}
